'use client';

import { useState } from 'react';
import { IconBrandGoogle } from '@tabler/icons-react';
import { signIn } from 'next-auth/react';
import { Anchor, Button, Group, Stack } from '@mantine/core';
import { MicrosoftLogo } from './MicrosoftLogo';
import classes from './SignInOptions.module.css';

interface SignInOptionsProps {
  /** Which providers `auth.ts` actually registered. */
  providers: { microsoftEntraId: boolean; google: boolean };
  /** Where to land after a successful sign-in. */
  callbackUrl: string;
}

const SignInOptions = ({ providers, callbackUrl }: SignInOptionsProps) => {
  const [pending, setPending] = useState<string | null>(null);

  const startSignIn = (provider: string) => {
    setPending(provider);
    signIn(provider, { callbackUrl });
  };

  return (
    <Stack gap={0} className={classes.options}>
      {providers.microsoftEntraId && (
        <Button
          fullWidth
          variant="default"
          className={classes.providerButton}
          classNames={{ section: classes.providerButtonIcon }}
          leftSection={<MicrosoftLogo />}
          loading={pending === 'microsoft-entra-id'}
          disabled={pending !== null && pending !== 'microsoft-entra-id'}
          onClick={() => startSignIn('microsoft-entra-id')}
        >
          Sign in with Microsoft
        </Button>
      )}

      {/* Hand-rolled rather than Mantine's `Divider`, whose rule colour is defined on `:root` per
          colour scheme and so cannot be pinned to this deliberately light-only card. */}
      {providers.microsoftEntraId && providers.google && (
        <div className={classes.divider}>
          <span className={classes.dividerLine} />
          <span className={classes.dividerLabel}>or</span>
          <span className={classes.dividerLine} />
        </div>
      )}

      {providers.google &&
        (providers.microsoftEntraId ? (
          <Group justify="center">
            <Anchor
              component="button"
              type="button"
              className={classes.legacyLink}
              onClick={() => startSignIn('google')}
            >
              Use legacy Google login
            </Anchor>
          </Group>
        ) : (
          // Microsoft is not configured yet, so Google is the only way in and gets the button.
          <Button
            fullWidth
            variant="default"
            className={classes.providerButton}
            classNames={{ section: classes.providerButtonIcon }}
            leftSection={<IconBrandGoogle size={18} stroke={1.5} />}
            loading={pending === 'google'}
            onClick={() => startSignIn('google')}
          >
            Sign in with Google
          </Button>
        ))}
    </Stack>
  );
};

export { SignInOptions };
